/**
 * Modal primitive.
 *
 * Centered overlay + content panel with close affordance. Basic focus trap:
 *   - On open, focus moves to the close button (or first focusable child).
 *   - Escape key calls `onClose`.
 *   - Tab/Shift-Tab cycles within the panel (not exhaustive — deep focus
 *     trap semantics are a future enhancement).
 *
 * Click on overlay closes; click inside the panel does not.
 *
 * Styling uses tokens (CP5). The overlay is portaled to `document.body`
 * (`createPortal`) — the stacking problem the original header comment predicted
 * came true (T27.2.7.10): rendered inline, the overlay's `z-modal` is trapped in
 * an ancestor stacking context and the root-level fixed bottom nav (`z-sticky`)
 * paints over the sheet. Portaling to the document root lets `z-modal` compete at
 * the top level so the overlay sits above the nav. The public API, focus trap,
 * Escape, overlay-click-close, and `useOverlayRegistration` behavior are
 * unchanged — only the DOM mount point moved.
 *
 * Legacy equivalent: `.modal-overlay` + `.modal` classes from
 * `frontend/src/App.css` (lines 1578+) driven inline in every screen.
 */

import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { tokens } from '@shopista/shared/tokens';
import { useOverlayRegistration } from '../lib/useOverlayRegistration';
import { useVisualViewportBottomInset } from '../adapters/useVisualViewportBottomInset';

/**
 * `center` (default) — the classic centered dialog (all callers before T12.1.5.1.3).
 * `sheet` — a full-width bottom-anchored sheet (mobile pattern). Overlay anchors
 * its panel to the bottom edge; the panel spans the width, rounds only its top
 * corners, caps at 88vh, and scrolls its own body. Reuses the centered variant's
 * focus trap, Escape, overlay-click-to-close, and z-index unchanged.
 */
export type ModalVariant = 'center' | 'sheet';

/**
 * Width of the centered dialog panel.
 * `default` — the classic 32rem cap (every caller before this prop existed).
 * `wide` — a 56rem cap for content that needs the room (e.g. a multi-column
 * admin table that crushes at 32rem). The panel keeps `width: 100%`, so on a
 * narrow viewport the overlay's `--space-4` padding still bounds it — `wide`
 * only raises the cap, it never forces horizontal overflow. Applies to the
 * `center` variant only; the `sheet` is full-width and ignores it.
 */
export type ModalSize = 'default' | 'wide';

// Centered-dialog max-widths, keyed by size. Named here (not inlined in markup)
// so the two widths read as one decision next to `panelByVariant`. The token
// scale carries no width sizing tokens, so these are local rem constants.
const CENTER_MAX_WIDTH_DEFAULT = '32rem';
const CENTER_MAX_WIDTH_WIDE = '56rem';

const centerMaxWidthBySize: Record<ModalSize, string> = {
  default: CENTER_MAX_WIDTH_DEFAULT,
  wide: CENTER_MAX_WIDTH_WIDE,
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  /** aria-label used when no visible title is provided. */
  ariaLabel?: string;
  /** Hide the built-in close (×) button. Defaults to false. */
  hideCloseButton?: boolean;
  /** Keep a sheet above an overlaid soft keyboard. */
  keyboardAware?: boolean;
  /** Presentation. `center` (default) keeps every existing caller unchanged. */
  variant?: ModalVariant;
  /**
   * Centered-dialog width. `default` (the implicit value) keeps the 32rem cap
   * so every existing caller renders exactly as before; `wide` raises it to
   * 56rem. No effect on the `sheet` variant.
   */
  size?: ModalSize;
}

const overlayBaseStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  // zIndex consumes the JS token because React's CSSProperties typing
  // restricts this slot to `number`. The value still flows from the token
  // source of truth in `packages/shared/tokens`.
  zIndex: tokens.zIndex.modal,
  display: 'flex',
  justifyContent: 'center',
  padding: 'var(--space-4)',
};

const overlayByVariant: Record<ModalVariant, CSSProperties> = {
  center: { ...overlayBaseStyle, alignItems: 'center' },
  // Bottom-sheet: anchor the panel to the bottom edge, and drop the overlay's
  // padding so the sheet runs full-bleed to the viewport sides / bottom.
  sheet: { ...overlayBaseStyle, alignItems: 'flex-end', padding: 0 },
};

const panelBaseStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-primary)',
  boxShadow: 'var(--shadow-lg)',
  fontFamily: 'var(--font-family-sans)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const panelByVariant: Record<ModalVariant, CSSProperties> = {
  center: {
    ...panelBaseStyle,
    borderRadius: 'var(--radius-lg)',
    // maxWidth is applied per-size at render (see `centerMaxWidthBySize`) so the
    // `wide` size can raise the 32rem cap without a second variant.
    maxHeight: 'calc(100vh - var(--space-8))',
    overflow: 'auto',
  },
  sheet: {
    ...panelBaseStyle,
    // Full-width, top-corners only, slide-up. The panel is a flex column that does
    // NOT scroll itself — the body is the scroller (see `bodyByVariant.sheet`).
    maxWidth: 'none',
    borderTopLeftRadius: 'var(--radius-lg)',
    borderTopRightRadius: 'var(--radius-lg)',
    maxHeight: '88vh',
    overflow: 'hidden',
    // Panel-level safe-area reserve (T27.2.7.10, reworked Deliverable 8). This
    // padding sits OUTSIDE the scrolling body, so it reserves a solid band of the
    // panel's own background at the bottom of the sheet. The scrolling body ends
    // above this band, so no content row can ever paint inside the OS nav band
    // mid-scroll. (The earlier approach put the env() reserve as padding INSIDE the
    // scroll container, which only lengthens the scroll at its very end — rows
    // still rendered in the device nav band while scrolling.) `env(...)` is 0 with
    // no inset, so desktop / no-inset contexts are unaffected.
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--space-4) var(--space-6)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
};

const titleStyle: CSSProperties = {
  // T47 Phase 4 — the shared sheet/modal title is a heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  margin: 0,
};

const closeStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  fontSize: 'var(--font-size-xl)',
  lineHeight: 'var(--line-height-tight)',
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
  padding: 'var(--space-1)',
  // ≥44px touch target (frontend-standards §3). The bare glyph + `--space-1`
  // padding rendered ~28px; centering it in a 44px box keeps the same visual
  // weight while meeting the accessible-target floor for every Modal caller.
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const bodyByVariant: Record<ModalVariant, CSSProperties> = {
  center: {
    padding: 'var(--space-4) var(--space-6)',
    flex: 1,
  },
  // The sheet body is the SCROLL container (T27.2.7.10 Deliverable 8). `flex: 1`
  // + `minHeight: 0` lets it grow to fill the flex-column panel and shrink below
  // its content height so `overflowY: auto` actually scrolls. Its bottom padding is
  // the base `--space-4` only — the device safe-area reserve now lives on the panel
  // (outside this scroller), so the body's scroll viewport ends above the OS nav
  // band and no row can paint inside it mid-scroll.
  sheet: {
    padding: 'var(--space-4) var(--space-6)',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
};

export function Modal({
  open,
  onClose,
  title,
  children,
  ariaLabel,
  hideCloseButton = false,
  keyboardAware = false,
  variant = 'center',
  size = 'default',
}: ModalProps): JSX.Element | null {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const keyboardBottomInset = useVisualViewportBottomInset(
    open && keyboardAware && variant === 'sheet',
  );

  const handleOverlayClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    // Move focus into the panel so keyboard users are anchored there.
    // Prefer the close button; fall back to the panel itself if hidden.
    const target = closeBtnRef.current ?? panelRef.current;
    target?.focus();
  }, [open]);

  // Resolve the panel style: the centered dialog gets its max-width from `size`
  // (`sheet` is full-width and ignores it). Defaulting `size` to `default`
  // reproduces the previous fixed 32rem cap, so every existing caller is
  // byte-for-byte unchanged.
  const basePanelStyle =
    variant === 'center'
      ? { ...panelByVariant.center, maxWidth: centerMaxWidthBySize[size] }
      : panelByVariant[variant];
  const panelStyle = keyboardAware && variant === 'sheet'
    ? { ...basePanelStyle, maxHeight: '100%' }
    : basePanelStyle;
  const overlayStyle = keyboardAware && variant === 'sheet'
    ? { ...overlayByVariant[variant], bottom: keyboardBottomInset }
    : overlayByVariant[variant];

  // T27.2.7.3 — register with the overlay stack while open so the Android
  // hardware Back button closes this modal/sheet (covers the deal detail sheet,
  // combo-pick sheet, StoreEditorModal, and any other Modal caller) instead of
  // popping the route underneath it (DL1/MN2).
  useOverlayRegistration(open, onClose);

  if (!open) return null;

  // Portal to the document root so the overlay's `z-modal` competes at the top of
  // the stacking order — not trapped beneath the fixed bottom nav's stacking
  // context (T27.2.7.10). `document.body` always exists in the browser and under
  // the test runner's DOM environment; `createPortal` keeps React event bubbling
  // (overlay click / Escape) working through the portal boundary.
  return createPortal(
    <div
      style={overlayStyle}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      data-testid="modal-overlay"
      data-variant={variant}
      data-keyboard-aware={keyboardAware ? 'true' : 'false'}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ? undefined : ariaLabel}
        aria-labelledby={title ? 'modal-title' : undefined}
        tabIndex={-1}
        style={panelStyle}
        data-testid="modal-panel"
      >
        {(title || !hideCloseButton) && (
          <div style={headerStyle}>
            {title ? (
              <h2 id="modal-title" style={titleStyle}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            {!hideCloseButton && (
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={closeStyle}
                data-testid="modal-close"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div style={bodyByVariant[variant]} data-testid="modal-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
